
import React, {useContext, useState} from "react";
import usersApi from "../../../../api/usersApi";
import {buyNowContext} from "../BuyNow";
import PropTypes from "prop-types";
import { useSimpleReactValidator } from "../../utils/useSimpleReactValidator";
import {SIGNUP_USER_TYPES} from "../../../../constants";

const CreateAccountForm = ({ onAccountCreated, appName, signupUserType }) => {
  const validator = useSimpleReactValidator();
  const { shippingStageState, order, portal } = useContext(buyNowContext);

  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [licenseKeyText, setLicenseKeyText] = useState(order.promoCode?.licenseKey?.key || order.licenseKeys[0].key);
  const [newUserData, setNewUserData] = useState({
    firstName: shippingStageState.firstName,
    lastName: shippingStageState.lastName,
    email: shippingStageState.email,
    phone: shippingStageState.phone,
    password: "",
    passwordConfirmation: "",
    signupUserType
  });

  /**
   * A helper method to set a field of the newUserData object.
   * @param {Object} e - Event from an input element.
   * @param {String} fieldName - Name of the field to update within newUserData.
   */
  const updateNewUserField = (e, fieldName) => {
    const updateObj = {};
    updateObj[fieldName] = e.target.value;
    setNewUserData({ ...newUserData, ...updateObj })
  };

  /**
   * Make an API request to create a user from the given state of this component.
   */
  const createUser = () => {
    if (validator.current.allValid()) {
      validator.current.hideMessages();
    } else {
      validator.current.showMessages();
      return;
    }

    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    // Reset the error message while we attempt to create the user.
    setErrorMessage(null);

    usersApi.createUser(newUserData, licenseKeyText, portal)
      .then(user => {
        // Although the user was created, they have not been logged into the website. Do this now.
        // Note that we are returning the user in the promise chain, not the response to webLogin().
        return usersApi.webLogin(user.email, newUserData.password).then(() => user);
      })
      .then(user => {
        onAccountCreated(user);
      })
      .catch(responseErrorMessage => {
        setErrorMessage(responseErrorMessage);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <div className="row mb-5">
      <form className="row g-3 px-3 p-lg-0 col-lg-11 ms-lg-0 mt-3 mb-0 pt-0" id={"create-from"}>
        <p className="header-txt text-dark text-start fw-bold">Create account</p>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-6">
              <input type="text" className="form-control form-control-inputs" id="firstName" placeholder="First name"
                     required={true}
                     value={newUserData.firstName}
                     onChange={e => updateNewUserField(e, "firstName")}
              />

              {validator.current.message('firstName', newUserData.firstName, 'required')}
            </div>

            <div className="col-md-6">
              <input type="text" className="form-control form-control-inputs" id="lastName" placeholder="Last name"
                     required={true}
                     value={newUserData.lastName}
                     onChange={e => updateNewUserField(e, "lastName")}
              />

              {validator.current.message('lastName', newUserData.lastName, 'required')}
            </div>
          </div>
        </div>

        <div className="col-12">
          <input type="email" className="form-control form-control-inputs" id="email" placeholder="Email"
                 required={true}
                 value={newUserData.email}
                 onChange={e => updateNewUserField(e, "email")}
          />

          {validator.current.message('email', newUserData.email, 'required|email')}
        </div>

        <div className="col-12">
          <input type="tel" className="form-control form-control-inputs" id="phone" placeholder="Phone number"
                 required={true}
                 value={newUserData.phone}
                 onChange={e => updateNewUserField(e, "phone")}
          />
        </div>

        <div className="col-12">
          <input type="password" className="form-control form-control-inputs" id="password"
                 required={true}
                 placeholder="Password"
                 value={newUserData.password}
                 onChange={e => updateNewUserField(e, "password")}
          />

          {validator.current.message('password', newUserData.password, 'required')}
        </div>

        <div className="col-12">
          <input type="password" className="form-control form-control-inputs" id="confirmPassword"
                 required={true}
                 placeholder="Confirm password"
                 value={newUserData.passwordConfirmation}
                 onChange={e => updateNewUserField(e, "passwordConfirmation")}
          />

          {validator.current.message('passwordConfirmation', newUserData.passwordConfirmation, `required|in:${newUserData.password}`, { messages: { in: "Password confirmation doesn't match password!" } })}
        </div>

        <div className="col-12">
          <input type="text" className="form-control form-control-inputs" id="uniqueLicenseKey"
                 placeholder="Unique license key"
                 disabled={true}
                 value={licenseKeyText}
                 onChange={e => setLicenseKeyText(e.target.value)}
          />

          <p className="sub-txt text-dark text-start pt-1">
            {`This license key is linked to the ${appName} scale and subscription you just purchased.`}
          </p>
        </div>
      </form>

      <div className="offset-lg-2 col-lg-6 d-flex flex-column">
        <a className="teal button-link next-button-txt next-step-button px-lg-3 lg-100-sm-75 align-self-center" onClick={createUser}>
          {isProcessing ? "Processing..." : "Create my account"}
        </a>

        {errorMessage &&
          <p className="plan-main-txt text-danger">
            <br/>
            {errorMessage}
          </p>
        }
      </div>
    </div>
  );
};

CreateAccountForm.defaultProps = {
  onAccountCreated: () => {},
  signupUserType: SIGNUP_USER_TYPES.DEFAULT
};

CreateAccountForm.propTypes = {
  onAccountCreated: PropTypes.func,
  signupUserType: PropTypes.string,
  appName: PropTypes.string.isRequired
}

export default CreateAccountForm;